import Button from '@atlaskit/button';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useContextualMenu } from '@databases/contextual-menu';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import {
  deselectAll,
  selectFieldSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

export const FieldSettingsButtonContainer = styled.div`
  opacity: 0;
  transition: opacity 0.2s;
`;

export interface FieldSettingsButtonProps {
  contentId: string;
  fieldId: string;
}

const FieldSettingsButton: FunctionComponent<FieldSettingsButtonProps> = ({
  contentId,
  fieldId,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { view } = useView();
  const viewSettings = view.settings;

  const client = useAnalytics();
  const { formatMessage } = useIntl();
  const { showPanel } = usePanel();
  const { closeMenu } = useContextualMenu();

  const handleSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      // prevents propagation to DatabaseLayout, where we deselect all cells on click
      e.stopPropagation();

      dispatch(deselectAll());
      dispatch(
        selectFieldSingle({
          contentId,
          scope: 'main',
          viewSettings,
          fieldId,
        })
      );
      dispatch(setSoftSelection({ value: true }));
      showPanel('FieldSettingsPanel', { fieldId });
      closeMenu();

      client.sendDatabaseFieldUpdatedEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [dispatch, contentId, viewSettings, fieldId, showPanel, closeMenu, client]
  );

  const settingsButton = (
    <Button
      id="field-settings-button"
      onClick={handleSettingsClick}
      appearance="subtle"
      spacing="compact"
      iconBefore={
        <SettingsIcon
          size="small"
          label={formatMessage(messages['table-layout.field-settings.label'])}
        />
      }
    />
  );

  return <FieldSettingsButtonContainer>{settingsButton}</FieldSettingsButtonContainer>;
};

export default FieldSettingsButton;
